import {
  Center,
  HStack,
  Text,
  Box,
  VStack,
  Icon,
  ScrollView,
  Heading,
  Spinner,
  Pressable,
  Switch,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowLeft } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AxiosError } from 'axios';

import { useQueryClient } from '@tanstack/react-query';
import type { StackNavigationProp } from '@react-navigation/stack';
import { flatToNestedEvaluationDeskParams } from '@/common/navigation/evaluationDeskLinking';
import { useRunJobInEngine } from '../../common/api/endpoints/jobs/jobs';
import {
  buildTaskFormEvaluationFromApi,
  formTaskEvaluationToModuleSavePayload,
  resolveOralExamDimensionKeys,
} from './utils/evaluationFormMapping';
import { useGetTasksByUserCourseModule, getGetTasksByUserCourseModuleQueryKey } from '../../common/api/endpoints/tasks/tasks';
import { getGetEvaluationsForLecturerQueryKey } from '../../common/api/endpoints/evaluations/evaluations';
import {
  useEvaluateModuleSubmission,
  useGetModuleSubmissionEvaluation,
  getGetModuleSubmissionEvaluationQueryKey,
} from '../../common/api/endpoints/submission-evaluation/submission-evaluation';
import EvaluationForm from './components/EvaluationForm';
import AutogradeControl from './components/AutogradeControl';
import AutogradeJobWatcher, {
  buildAutogradeJobKey,
  type AutogradeJob,
} from './components/AutogradeJobWatcher';
import { EvaluationFilters } from './components/EvaluationFilters';
import { EvaluationTableHeader } from './components/EvaluationTableHeader';
import { EvaluationTableRow } from './components/EvaluationTableRow';
import { EvaluationTablePagination } from './components/EvaluationTablePagination';

import { useAuthStore } from '../../common/auth/store';
import { useDialog } from '../../common/context/DialogContext';
import {
  useTenantEventKinds,
  useTenantGradingSettings,
} from '../../common/context/TenantSettingsStore';
import {
  EvaluationGradingConfigProvider,
} from './context/EvaluationGradingConfigContext';
import {
  taskRequiresEvaluation,
  isLecturerScoredTask,
  isTaskHiddenWhenAiTutoredFiltered,
} from '../../common/utils/taskEvaluationUtils';
import { evaluateModuleSubmissionBody } from '../../common/api/endpoints/submission-evaluation/submission-evaluation.zod';
import log from '../../common/utils/logger';
import { useEvaluationTable } from './hooks/useEvaluationTable';
import type { ExtendedSubmission, TaskFormEvaluation, EvaluationFormData } from './types';
import type { LecturerRouteParamList, LecturerModule } from '../../common/navigation/RoleNavigations/Lecturer/types';
import type { PendingEvaluation } from '../../common/api/models';

interface RouteParams {
  module?: {
    id: string;
    title: string;
  };
  submission?: ExtendedSubmission;
  /** Flat URL segments — present when linking parse was not nested yet. */
  moduleId?: string;
  userId?: string;
  courseId?: string;
}

const HIDE_AI_TUTORED_KEY = '@flare/evalDesk/hideAiTutored';

/** Job-engine success payload is an untyped API envelope; only a string job id starts the stream. */
const readStartedJobId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object' || !('jobId' in payload)) return undefined;
  const { jobId } = payload as { jobId?: unknown };
  return typeof jobId === 'string' && jobId.length > 0 ? jobId : undefined;
};

const EvaluationDesk: React.FC<{ route: { params?: RouteParams } }> = ({ route }) => {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const navigation = useNavigation<StackNavigationProp<LecturerRouteParamList>>();
  const queryClient = useQueryClient();
  const { showToastMessage } = useDialog();
  const { evalDimensionKeys: tenantEvalDimensionKeys, oralExamDimensionKeys } = useTenantGradingSettings();
  const { eventKinds } = useTenantEventKinds();

  // Hide AI-tutored tasks toggle (default ON, persisted per-user)
  const [hideAiTutored, setHideAiTutoredState] = useState(true);
  const [autogradeJobs, setAutogradeJobs] = useState<Record<string, AutogradeJob>>({});
  const autogradeJobsRef = useRef<Record<string, AutogradeJob>>({});
  const runJobMutation = useRunJobInEngine();
  useEffect(() => {
    AsyncStorage.getItem(HIDE_AI_TUTORED_KEY)
      .then((stored) => {
        if (stored !== null) setHideAiTutoredState(stored === 'true');
      })
      .catch((err) => log.warn('Failed to load hideAiTutored preference', err));
  }, []);
  const setHideAiTutored = useCallback((value: boolean) => {
    setHideAiTutoredState(value);
    AsyncStorage.setItem(HIDE_AI_TUTORED_KEY, String(value)).catch((err) => {
      log.warn('Failed to persist hideAiTutored preference', err);
    });
  }, []);
  const nestedParams = useMemo((): RouteParams => {
    if (route.params?.module || route.params?.submission) {
      return route.params;
    }
    const fromFlat = flatToNestedEvaluationDeskParams({
      moduleId: route.params?.moduleId,
      userId: route.params?.userId,
      courseId: route.params?.courseId,
    });
    return {
      module: fromFlat.module
        ? { id: fromFlat.module.id, title: fromFlat.module.title ?? '' }
        : undefined,
      submission: fromFlat.submission as ExtendedSubmission | undefined,
    };
  }, [route.params]);

  const selectedModule = useMemo(() => {
    if (nestedParams.module) {
      return { id: nestedParams.module.id, title: nestedParams.module.title ?? '' };
    }
    const moduleId = nestedParams.submission?.moduleId;
    if (moduleId) {
      return { id: moduleId, title: String(nestedParams.submission?.moduleName ?? '') };
    }
    return undefined;
  }, [nestedParams]);

  // Use TanStack Table for evaluations with sorting, filtering, and pagination
  // Error handling is done inside the hook via onError callback
  const {
    evaluations: evaluationsList,
    isLoading: isLoadingEvaluations,
    page,
    setPage,
    pageSize,
    totalItems,
    totalPages,
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
  } = useEvaluationTable(selectedModule?.id);

  const submissionFromParams = nestedParams?.submission;

  const selectedEvaluation = useMemo((): ExtendedSubmission | undefined => {
    if (!submissionFromParams) return undefined;

    const row = evaluationsList.find(
      (evaluation) => evaluation.userId === submissionFromParams.userId
        && evaluation.moduleId === submissionFromParams.moduleId
        && evaluation.courseId === submissionFromParams.courseId,
    );

    if (row) {
      return {
        ...submissionFromParams,
        userId: row.userId,
        moduleId: row.moduleId,
        courseId: row.courseId,
        moduleName: row.module || submissionFromParams.moduleName,
        userName: row.student || submissionFromParams.userName,
        dueDate: row.dueDate,
        submissionDate: row.submittedAt,
        grading: row.grade ? { grade: String(row.grade) } : submissionFromParams.grading,
        id: `${row.userId}-${row.moduleId}`,
        state: row.state,
      } as ExtendedSubmission;
    }

    return submissionFromParams as ExtendedSubmission;
  }, [submissionFromParams, evaluationsList]);

  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    isError: isTasksError,
    error: tasksError,
    refetch: refetchTasks,
  } = useGetTasksByUserCourseModule(
    selectedEvaluation?.userId || '',
    selectedEvaluation?.courseId || '',
    selectedEvaluation?.moduleId || '',
    { include: 'submissions' },
    {
      query: {
        enabled: !!(selectedEvaluation?.userId && selectedEvaluation?.courseId && selectedEvaluation?.moduleId),
        // don't refetch automatically as this will reset form data although user might be working on it
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        select: (data) => data.data.data,
      },
    },
  );

  const {
    data: moduleEvaluationData,
    isLoading: isLoadingModuleEval,
    isError: isModuleEvalError,
    error: moduleEvalError,
  } = useGetModuleSubmissionEvaluation(
    selectedEvaluation?.userId || '',
    selectedEvaluation?.courseId || '',
    selectedEvaluation?.moduleId || '',
    {
      query: {
        enabled: !!(selectedEvaluation?.userId && selectedEvaluation?.courseId && selectedEvaluation?.moduleId),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        select: (data) => data.data.data,
      },
    },
  );

  // Graded submissions stay read-only. A deep link may omit route state; the loaded record is the same fact.
  const isReadOnly = (selectedEvaluation?.state ?? moduleEvaluationData?.state) === 'graded';
  // A grade recorded by someone else is off this lecturer's desk, including a direct link.
  const gradedBySomeoneElse = moduleEvaluationData?.state === 'graded'
    && !!moduleEvaluationData.grading?.evaluatorId
    && moduleEvaluationData.grading.evaluatorId !== currentUserId;

  const gradingConfigSnapshot = moduleEvaluationData?.gradingConfigSnapshot ?? null;
  const evalDimensionKeys = useMemo(() => (
    gradingConfigSnapshot?.evalDimensions
      ? Object.keys(gradingConfigSnapshot.evalDimensions)
      : tenantEvalDimensionKeys
  ), [gradingConfigSnapshot?.evalDimensions, tenantEvalDimensionKeys]);

  // Combine grading from initial list and full detail query
  const combinedGrading = useMemo(() => {
    if (!moduleEvaluationData?.grading) return selectedEvaluation?.grading;

    const moduleGrading = moduleEvaluationData.grading;

    return {
      comment: moduleGrading.feedback || selectedEvaluation?.grading?.comment,
      manualGrade: moduleGrading.manualGrade === true,
      grade: moduleGrading.grade,
    };
  }, [moduleEvaluationData, selectedEvaluation?.grading]);

  // Exam session GET already carries studentName (and version). URL flatten drops
  // display names; do not fetch user/module just to paint the header.
  const headerStudentName = (
    selectedEvaluation?.userName?.trim()
    || moduleEvaluationData?.studentName?.trim()
    || undefined
  );
  const headerModuleTitle = (
    selectedEvaluation?.moduleName
    || selectedModule?.title
    || ''
  );

  // Prepare tasks array and submissions object for the form
  const { tasks, evaluations } = useMemo(() => {
    if (!tasksData || !selectedEvaluation) return { tasks: [], evaluations: {} as Record<string, TaskFormEvaluation> };

    // Tasks array with submissions for display.
    // When hideAiTutored is on, drop AI-tutored (auto-evaluated) and pure event
    // tasks since the lecturer has no manual grading work for either. MCQ and
    // oral exam tasks always remain visible.
    const visibleTasksData = hideAiTutored
      ? tasksData.filter((task) => !isTaskHiddenWhenAiTutoredFiltered(task))
      : tasksData;

    const tasks = visibleTasksData.map((task) => ({
      ...task,
      submission: {
        ...task.submission,
        // Preserve these for display/initialization consistency
        evaluation: task.submission?.evaluation || null,
        feedback: task.submission?.feedback || null,
      },
    }));

    // Evaluations object formatted for the mutation
    // Only include tasks that require evaluation to avoid backend errors
    const evaluations = tasksData.reduce((acc, task) => {
      if (task.id && taskRequiresEvaluation(task, eventKinds)) {
        acc[task.id] = buildTaskFormEvaluationFromApi(
          task,
          evalDimensionKeys,
          resolveOralExamDimensionKeys(task, oralExamDimensionKeys),
        );
      }
      return acc;
    }, {} as Record<string, TaskFormEvaluation>);
    return { tasks, evaluations };
  }, [tasksData, selectedEvaluation, eventKinds, evalDimensionKeys, oralExamDimensionKeys, hideAiTutored]);

  const moduleVersion = useMemo(() => {
    if (moduleEvaluationData?.moduleVersion) {
      return moduleEvaluationData.moduleVersion;
    }
    if (tasksData && tasksData.length > 0 && tasksData[0].version) {
      return tasksData[0].version;
    }
    return (selectedModule as LecturerModule)?.version;
  }, [moduleEvaluationData?.moduleVersion, tasksData, selectedModule]);

  const tasksQueryKey = useMemo(
    () => getGetTasksByUserCourseModuleQueryKey(
      selectedEvaluation?.userId || '',
      selectedEvaluation?.courseId || '',
      selectedEvaluation?.moduleId || '',
      { include: 'submissions' },
    ),
    [selectedEvaluation?.userId, selectedEvaluation?.courseId, selectedEvaluation?.moduleId],
  );
  const evaluateModuleSubmissionMutation = useEvaluateModuleSubmission();

  const selectedSubmissionQueryKey = useMemo(
    () => `${selectedEvaluation?.userId || ''}-${selectedEvaluation?.courseId || ''}-${selectedEvaluation?.moduleId || ''}`,
    [selectedEvaluation?.userId, selectedEvaluation?.courseId, selectedEvaluation?.moduleId],
  );
  const hasReportedSubmissionLoadError = useRef(false);

  useEffect(() => {
    hasReportedSubmissionLoadError.current = false;
  }, [selectedSubmissionQueryKey]);

  const submissionLoadFailed = isTasksError || isModuleEvalError;

  const submissionLoadErrorMessage = useMemo(() => {
    if (!submissionLoadFailed) return null;

    const isForbidden = [tasksError, moduleEvalError].some(
      (error) => error instanceof AxiosError && error.response?.status === 403,
    );

    return isForbidden
      ? t('lecturer.evalDesk.submissionPermissionDenied')
      : t('lecturer.evalDesk.submissionLoadError');
  }, [submissionLoadFailed, tasksError, moduleEvalError, t]);

  useEffect(() => {
    if (!submissionLoadErrorMessage || hasReportedSubmissionLoadError.current) {
      return;
    }

    hasReportedSubmissionLoadError.current = true;
    showToastMessage('error', submissionLoadErrorMessage);
  }, [submissionLoadErrorMessage, showToastMessage]);

  useEffect(() => {
    autogradeJobsRef.current = autogradeJobs;
  }, [autogradeJobs]);

  const removeAutogradeJob = useCallback((key: string) => {
    setAutogradeJobs((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleAutogradeProgress = useCallback((key: string, progress: number) => {
    setAutogradeJobs((prev) => {
      const job = prev[key];
      if (!job || job.progress === progress) return prev;
      return { ...prev, [key]: { ...job, progress } };
    });
  }, []);

  const handleAutogradeComplete = useCallback(async (key: string) => {
    const job = autogradeJobsRef.current[key];
    if (!job) return;

    try {
      // The form copies task feedback and level dots only when the running
      // flag falls. Refetch first so that copy sees the graded tasks.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: job.tasksQueryKey, type: 'all' }),
        queryClient.refetchQueries({ queryKey: job.moduleEvaluationQueryKey, type: 'all' }),
      ]);
      showToastMessage('success', 'AutoGrading completed successfully', 'AutoGrading');
    } finally {
      removeAutogradeJob(key);
    }
  }, [queryClient, removeAutogradeJob, showToastMessage]);

  const handleAutogradeError = useCallback((key: string, error: unknown) => {
    removeAutogradeJob(key);
    showToastMessage('error', 'AutoGrading failed', 'AutoGrading');
    log.info('Error in AutoGrading: ', error);
  }, [removeAutogradeJob, showToastMessage]);

  const handleRequestAutograde = useCallback(async (
    userId: string,
    courseId: string,
    moduleId: string,
  ) => {
    const key = buildAutogradeJobKey(userId, courseId, moduleId);
    const tasksKey = getGetTasksByUserCourseModuleQueryKey(
      userId,
      courseId,
      moduleId,
      { include: 'submissions' },
    );
    const moduleEvalKey = getGetModuleSubmissionEvaluationQueryKey(userId, courseId, moduleId);

    const newJob: AutogradeJob = {
      key,
      userId,
      courseId,
      moduleId,
      progress: 0,
      tasksQueryKey: tasksKey,
      moduleEvaluationQueryKey: moduleEvalKey,
    };

    setAutogradeJobs((prev) => ({ ...prev, [key]: newJob }));
    showToastMessage('info', 'AutoGrading submissions...', 'AutoGrading');

    try {
      const response = await runJobMutation.mutateAsync({
        data: {
          type: 'autograde',
          params: { userId, moduleId, courseId },
        },
      });
      const jobId = readStartedJobId(response?.data?.data);
      if (!jobId) {
        removeAutogradeJob(key);
        handleAutogradeError(key, 'AutoGrading failed, no job started');
        return;
      }
      setAutogradeJobs((prev) => {
        const existing = prev[key];
        if (!existing) return prev;
        return { ...prev, [key]: { ...existing, jobId } };
      });
    } catch (error) {
      removeAutogradeJob(key);
      showToastMessage('error', 'AutoGrading failed, perhaps it\'s already running?', 'AutoGrading');
      log.info('Error starting AutoGrading: ', error);
    }
  }, [
    handleAutogradeError,
    removeAutogradeJob,
    runJobMutation,
    showToastMessage,
  ]);

  const autogradeJobWatchers = useMemo(
    () => Object.values(autogradeJobs).map((job) => (
      <AutogradeJobWatcher
        key={job.key}
        job={job}
        onProgress={handleAutogradeProgress}
        onComplete={handleAutogradeComplete}
        onError={handleAutogradeError}
      />
    )),
    [autogradeJobs, handleAutogradeComplete, handleAutogradeError, handleAutogradeProgress],
  );

  const openSubmissionAutogradeKey = selectedEvaluation
    ? buildAutogradeJobKey(
      selectedEvaluation.userId,
      selectedEvaluation.courseId,
      selectedEvaluation.moduleId,
    )
    : null;
  const isOpenSubmissionAutograding = openSubmissionAutogradeKey
    ? !!autogradeJobs[openSubmissionAutogradeKey]
    : false;
  const openSubmissionAutogradeProgress = openSubmissionAutogradeKey
    ? autogradeJobs[openSubmissionAutogradeKey]?.progress ?? 0
    : 0;

  // Header title is now managed by LecturerNavigation - no need to override here

  const navigateToEvaluationList = useCallback(() => {
    const listParams: LecturerRouteParamList['EvaluationDesk'] = selectedModule
      ? { module: selectedModule }
      : {};
    navigation.navigate({
      name: 'EvaluationDesk',
      params: listParams,
      merge: false,
    });
  }, [navigation, selectedModule]);

  useEffect(() => {
    if (gradedBySomeoneElse) {
      navigateToEvaluationList();
    }
  }, [gradedBySomeoneElse, navigateToEvaluationList]);

  const handleSave = async (data: EvaluationFormData, submitToStudent: boolean) => {
    if (!selectedEvaluation?.userId) {
      log.info('Save blocked - userId undefined. Source data:', {
        selectedEvaluation,
        routeParams: route.params?.submission,
      });
      showToastMessage('error', 'Cannot save evaluation: missing student information');
      return;
    }

    // Filter out tasks that don't require evaluation and process remaining ones
    const filteredTaskEvaluations: Record<string, ReturnType<typeof formTaskEvaluationToModuleSavePayload>> = {};
    Object.keys(data.taskEvaluations).forEach((taskId) => {
      const task = tasksData?.find((t) => t.id === taskId);
      if (task && isLecturerScoredTask(task, eventKinds)) {
        filteredTaskEvaluations[taskId] = formTaskEvaluationToModuleSavePayload(
          data.taskEvaluations[taskId],
          evalDimensionKeys,
          resolveOralExamDimensionKeys(task, oralExamDimensionKeys),
        );
      }
    });

    const parsedGrade = parseFloat(data.grading.grade);
    const requestData = {
      grading: {
        feedback: data.grading.feedback,
        grade: Number.isNaN(parsedGrade) ? 0 : parsedGrade,
        manualGrade: !data.grading.autoCalculateGrade,
      },
      taskEvaluations: filteredTaskEvaluations,
      submitToStudent,
    };

    // Validate payload
    evaluateModuleSubmissionBody.parse(requestData);

    await evaluateModuleSubmissionMutation.mutateAsync(
      {
        userId: selectedEvaluation.userId,
        courseId: selectedEvaluation.courseId,
        moduleId: selectedEvaluation.moduleId,
        data: requestData,
      },
      {
        onSuccess: () => {
          // Always invalidate the evaluations list
          queryClient.invalidateQueries({ queryKey: getGetEvaluationsForLecturerQueryKey() });

          if (submitToStudent) {
            showToastMessage('success', 'Evaluation submitted successfully to student');
            navigateToEvaluationList();
          } else {
            // Only invalidate tasks query when staying on the form
            showToastMessage('success', 'Evaluation saved successfully');
            const tasksKey = getGetTasksByUserCourseModuleQueryKey(
              selectedEvaluation.userId,
              selectedEvaluation.courseId,
              selectedEvaluation.moduleId,
              { include: 'submissions' },
            );
            queryClient.invalidateQueries({ queryKey: tasksKey });
            queryClient.invalidateQueries({
              queryKey: getGetModuleSubmissionEvaluationQueryKey(
                selectedEvaluation.userId,
                selectedEvaluation.courseId,
                selectedEvaluation.moduleId,
              ),
            });
          }
        },
      },
    );
  };

  // Handle going back to submission list
  const handleBackToList = () => {
    navigateToEvaluationList();
  };

  // Handle selecting a submission/evaluation
  const handleSelectSubmission = (evaluation: PendingEvaluation) => {
    if (evaluation.state === 'processing') {
      return;
    }

    // Convert PendingEvaluation to ExtendedSubmission format
    const submission: ExtendedSubmission = {
      userId: evaluation.userId,
      moduleId: evaluation.moduleId,
      courseId: evaluation.courseId,
      moduleName: evaluation.module,
      userName: evaluation.student,
      dueDate: evaluation.dueDate,
      submissionDate: evaluation.submittedAt,
      grading: evaluation.grade ? { grade: String(evaluation.grade) } : undefined,
      id: `${evaluation.userId}-${evaluation.moduleId}`,
      state: evaluation.state, // Pass state to allow correct isReadOnly calculation
    };

    const module = selectedModule ?? { id: evaluation.moduleId, title: evaluation.module };
    navigation.navigate('EvaluationDesk', { module, submission });
  };

  // Handle column sort changes
  const handleSortChange = (columnId: string) => {
    setSorting((prevSorting) => {
      const existingSort = prevSorting.find((s) => s.id === columnId);

      if (!existingSort) {
        // New sort - set to ascending
        return [{ id: columnId, desc: false }];
      }

      if (!existingSort.desc) {
        // Currently ascending - change to descending
        return [{ id: columnId, desc: true }];
      }

      // Currently descending - remove sort (back to default)
      return [{ id: 'submittedAt', desc: false }];
    });
  };

  // Evaluation form view (with autogradable check)
  const someTasksAutogradable = tasksData && tasksData.some((task) => !!task.rubric);

  let mainContent: React.ReactNode;

  // If no submission selected, or this grade belongs to someone else, show the list.
  if (!selectedEvaluation || gradedBySomeoneElse) {
    mainContent = (
      <Box flex={1} bg="$bgApp">
        {/* Header */}
        <HStack
          px="$6"
          py="$3"
          borderBottomWidth={1}
          borderBottomColor="$borderLight"
          alignItems="baseline"
          space="sm"
        >
          <Heading size="md" color="$textHeading">
            {selectedModule ? `Evaluations: ${selectedModule.title}` : 'Evaluations'}
          </Heading>
          <Text size="sm" color="$textSubtle">
            {isLoadingEvaluations ? 'Loading...' : `${totalItems} total`}
          </Text>
        </HStack>

        {/* Filters */}
        <EvaluationFilters
          onGlobalFilterChange={setGlobalFilter}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          isLoading={isLoadingEvaluations}
        />

        {/* Table */}
        {isLoadingEvaluations ? (
          <Center flex={1}>
            <Spinner size="large" />
          </Center>
        ) : evaluationsList && evaluationsList.length > 0 ? (
          <VStack flex={1}>
            <EvaluationTableHeader
              sorting={sorting}
              onSortChange={handleSortChange}
            />
            <ScrollView flex={1}>
              {evaluationsList.map((evaluation) => (
                <EvaluationTableRow
                  key={`${evaluation.userId}-${evaluation.module}`}
                  evaluation={evaluation}
                  onSelect={() => handleSelectSubmission(evaluation)}
                />
              ))}
            </ScrollView>
            <EvaluationTablePagination
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={setPage}
              isLoading={isLoadingEvaluations}
            />
          </VStack>
        ) : (
          <Center flex={1}>
            <VStack alignItems="center" space="md">
              <Text size="lg" color="$textSubtle">
                No evaluations found
              </Text>
              <Text size="sm" color="$textHint">
                {globalFilter || columnFilters.length > 0
                  ? 'Try adjusting your filters'
                  : 'All caught up!'}
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
    );
  } else if (isLoadingTasks || isLoadingModuleEval) {
    // Loading state for selected submission
    mainContent = (
      <Center flex={1} bg="$bgApp">
        <Spinner size="large" />
        <Text color="$textSubtle" mt="$4">
          Loading submission...
        </Text>
      </Center>
    );
  } else {
    mainContent = (
      <Box flex={1} bg="$bgApp">
        {/* Compact header: Back + Student name + AutoGrade */}
        <HStack
          px="$6"
          py="$3"
          borderBottomWidth={1}
          borderBottomColor="$borderLight"
          alignItems="center"
          justifyContent="space-between"
        >
          <HStack alignItems="center" space="md">
            <Pressable
              testID="evaluation-desk-back-to-list"
              onPress={handleBackToList}
              flexDirection="row"
              alignItems="center"
              p="$1"
              borderRadius="$md"
              $hover={{ backgroundColor: '$bgHover' }}
            >
              <Icon as={ArrowLeft} size="sm" color="$textSubtle" />
            </Pressable>
            <HStack alignItems="baseline" space="sm">
              <Text size="md" fontWeight="semibold" color="$textHeading">
                Submission of
                {' '}
                {headerStudentName || '…'}
              </Text>
              <Text size="sm" color="$textSubtle">
                {headerModuleTitle}
                {' '}
                (v
                {moduleVersion}
                )
              </Text>
              {isReadOnly && (
              <Text size="xs" color="$textSubtle" fontStyle="italic">
                read only
              </Text>
              )}
            </HStack>
          </HStack>

          <HStack alignItems="center" space="md">
            {/* Hide AI-tutored tasks toggle */}
            <HStack alignItems="center" space="xs">
              <Text size="xs" color="$textSubtle">
                {t('lecturer.evalDesk.hideAiTutoredLabel')}
              </Text>
              <Switch
                size="sm"
                value={hideAiTutored}
                onValueChange={setHideAiTutored}
              />
            </HStack>

            {/* AutoGrade button - top right */}
            {someTasksAutogradable && !isReadOnly && selectedEvaluation && (
            <AutogradeControl
              show
              isJobRunning={isOpenSubmissionAutograding}
              progress={openSubmissionAutogradeProgress}
              onRequestAutograde={() => handleRequestAutograde(
                selectedEvaluation.userId,
                selectedEvaluation.courseId,
                selectedEvaluation.moduleId,
              )}
              variant="secondary"
            />
            )}
          </HStack>
        </HStack>

        {/* Evaluation Form */}
        {submissionLoadFailed ? (
          <Center flex={1} px="$6" testID="evaluation-desk-load-error">
            <Text size="md" color="$statusError" textAlign="center">
              {submissionLoadErrorMessage}
            </Text>
          </Center>
        ) : tasksData && (
        <EvaluationGradingConfigProvider snapshot={gradingConfigSnapshot}>
          <EvaluationForm
            key={`evalform-${selectedEvaluation.userId}-${selectedEvaluation.courseId}-${selectedEvaluation.moduleId}`}
            module={selectedModule}
            moduleVersion={moduleVersion}
            tasks={tasks}
            eventKinds={eventKinds}
            selectedModuleSubmission={{
              courseId: selectedEvaluation.courseId,
              moduleId: selectedEvaluation.moduleId,
              userId: selectedEvaluation.userId,
              userName: headerStudentName,
            }}
            taskEvaluations={evaluations}
            grading={combinedGrading}
            isReadOnly={isReadOnly}
            onRequestSave={handleSave}
            onRequestRefresh={refetchTasks}
            tasksQueryKey={tasksQueryKey}
            hasActiveTaskFilter={hideAiTutored}
            isAutograding={isOpenSubmissionAutograding}
            moduleSubmissionId={moduleEvaluationData?.id}
          />
        </EvaluationGradingConfigProvider>
        )}
      </Box>
    );
  }

  return (
    <>
      {autogradeJobWatchers}
      {mainContent}
    </>
  );
};

export default EvaluationDesk;
